// components/advertiser-wallet/WalletView.tsx
import React from "react";
import { Wallet as WalletIcon, Star, Gift, Info } from "lucide-react";
import { TopRewardSpend } from "./types";
import StatCard from "../../../components/dashboard/stat-card-wallet";
import { formatCurrency, formatDateLong } from "../../../utils/function";

interface WalletViewProps {
  balance: number;
  isLowBalance: boolean;
  lowBalanceThreshold: number;
  quickFund: number;
  quickFundOptions: number[];
  formatNaira: (n: number) => string;
  runwayDays: number;
  walletStats: any[];
  topRewards: TopRewardSpend[];
  onSetLowBalance: () => void;
  onQuickFundSelect: (amount: number) => void;
  onAddFunds: () => void;
  onViewReport: () => void;
}

const WalletView: React.FC<WalletViewProps> = ({
  balance,
  isLowBalance,
  lowBalanceThreshold,
  quickFund,
  quickFundOptions,
  formatNaira,
  runwayDays,
  walletStats,
  topRewards,
  onSetLowBalance,
  onQuickFundSelect,
  onAddFunds,
  onViewReport,
}) => (
  <>
    {/* Stat cards */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
      {walletStats.map((s) => (
        <StatCard key={s.label} {...s} />
      ))}
    </div>

    {/* Wallet Balance + Reward Spend */}
    <div className="grid lg:grid-cols-2 gap-4">
      {/* Wallet Balance card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6">
        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500 mb-3">
          <WalletIcon size={16} />
        </div>
        <h2 className="font-semibold text-black text-base">Wallet Balance</h2>
        <p className="text-sm text-gray-400 mb-4">See how your links are faring</p>

        <p className="text-xs text-gray-500">Current Balance</p>
        <p
          className={`text-3xl font-semibold mt-1 ${
            isLowBalance ? "text-orange-500" : "text-black"
          }`}
        >
          {formatCurrency(balance || 0, 'NGN', 'code')}
        </p>
        <button
          onClick={onSetLowBalance}
          className="text-sm text-black underline underline-offset-2 mt-1"
        >
          Set Low Balance Alert: {formatNaira(lowBalanceThreshold)}
        </button>

        <p className="text-xs text-gray-500 mt-6 mb-2">Quick Fund</p>
        <div className="flex flex-wrap gap-2">
          {quickFundOptions.map((amt) => (
            <button
              key={amt}
              onClick={() => onQuickFundSelect(amt)}
              className={`px-4 py-2 rounded-full text-sm border ${
                quickFund === amt
                  ? "border-emerald-500 text-emerald-600 bg-emerald-50"
                  : "border-gray-200 text-gray-700"
              }`}
            >
              {formatCurrency(amt || 0, 'NGN', 'code')}
            </button>
          ))}
        </div>

        <button
          onClick={onAddFunds}
          className="w-full bg-black text-white rounded-xl py-3 mt-5 text-sm font-medium hover:bg-gray-900 transition-colors"
        >
          Add Funds
        </button>

        <div
          className={`flex gap-2 items-start rounded-xl px-3.5 py-3 mt-4 text-xs ${
            isLowBalance
              ? "bg-orange-50 text-orange-700"
              : "bg-gray-50 text-gray-500"
          }`}
        >
          <Info size={14} className="mt-0.5 shrink-0" />
          {isLowBalance ? (
            <span>
              <strong>Low Balance Alert:</strong> Your wallet balance is running
              low. Add funds to keep reward programs active.
            </span>
          ) : (
            <span>Always maintain sufficient funds to keep reward programs active.</span>
          )}
        </div>
      </div>

      {/* Top Reward Spends card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 flex flex-col">
        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500 mb-3">
          <Star size={16} />
        </div>
        <h2 className="font-semibold text-black text-base">Top Reward Spends</h2>
        <p className="text-sm text-gray-400 mb-4">
          Rewards consuming the largest share of your wallet
        </p>

        <div className="flex flex-col divide-y divide-gray-100 flex-1">
          {topRewards.slice(0, 3).map((item) => (
            <div key={item.reward_id} className="flex items-center gap-3 py-3 first:pt-0">
              {item.image_url ? (
                <img
                  src={item.image_url}
                  alt=""
                  className="w-11 h-11 rounded-lg object-cover shrink-0"
                />
              ) : (
                <div className="w-11 h-11 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-sm font-medium shrink-0">
                  {item.business_name?.charAt(0) || "?"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-black truncate">{item.business_name}</p>
                <p className="text-xs text-gray-400 truncate">{item.title}</p>
                <div className="flex items-center gap-1 text-xs text-emerald-600 mt-0.5">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      item.is_active ? "bg-emerald-500" : "bg-gray-300"
                    }`}
                  />
                  {item.is_active ? "Active Link" : "Inactive Link"}
                </div>
                <p className="text-[11px] text-gray-300 mt-0.5">
                  Created {formatDateLong(new Date(item.created_at))}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[11px] text-gray-400">All Time Spend</p>
                <p className="text-sm font-medium text-emerald-600">
                  {formatCurrency(item.all_time_spend || 0, 'NGN', 'symbol')}
                </p>
              </div>
            </div>
          ))}
          {topRewards.length === 0 && (
            <p className="text-sm text-gray-400 py-6 text-center">No reward spend yet.</p>
          )}
        </div>

        <button
          onClick={onViewReport}
          className="w-full bg-black text-white rounded-xl py-3 mt-5 text-sm font-medium hover:bg-gray-900 transition-colors"
        >
          View Full Report
        </button>
      </div>
    </div>
  </>
);

export default WalletView;